/**
 * Auth Service
 *
 * Manages authentication state and operations:
 * - Sign in/out with credentials or session
 * - Token decoding and state management
 * - Callback hooks for integrations (SSE, telemetry, router)
 */

import { inject } from "../../di";
import { jwtDecode } from "./jwt-decode";
import { queryClient } from "../../data-query-controller";
import type { IWritableAuthState, IAuthService, ICurrentUser, AuthRole } from "./types";
import type { IRpcService } from "../rpc/types";
import type { AuthResult } from "api-contracts/rpc/auth";
import type { ProfileDataResult } from "api-contracts/rpc/profile-data";

export class AuthService implements IAuthService {
  @inject<IWritableAuthState>("writableAuthState")
  private state!: IWritableAuthState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  /** Callbacks for sign-in events (for SSE, telemetry, etc.) */
  private signInCallbacks = new Set<(user: ICurrentUser) => void>();

  /** Callbacks for sign-out events */
  private signOutCallbacks = new Set<() => void>();

  /**
   * Sign in with an access token.
   * Decodes JWT and updates DI state.
   */
  signIn(accessToken: string): void {
    try {
      const payload = jwtDecode(accessToken) as {
        sub: string;
        name: string;
        roles: AuthRole[];
        exp: number;
        avatarUrl?: string;
        handle?: string;
        memberSinceDate?: string;
        preferences?: { themes?: string[]; chatDisplayName?: string };
        creatorsFollowing?: string[];
      } | null;

      if (!payload) {
        this.signOut();
        return;
      }

      const user: ICurrentUser = {
        id: payload.sub,
        name: payload.name,
        roles: payload.roles,
        avatarUrl: payload.avatarUrl,
        handle: payload.handle,
        memberSinceDate: payload.memberSinceDate,
        preferences: payload.preferences,
        creatorsFollowing: payload.creatorsFollowing,
      };

      // Update DI state
      this.state.accessToken.set(accessToken);
      this.state.currentUser.set(user);

      // Notify callbacks synchronously (for router updates, etc.)
      // This must be synchronous so router.currentUser is set
      // before any navigation happens in the sign-in page
      this.signInCallbacks.forEach((cb) => cb(user));

      // Load fresh profile data asynchronously (non-blocking)
      this.loadCurrentUserProfile();
    } catch (error) {
      console.error("[AuthService] Error during sign-in:", error);
      this.signOut();
    }
  }

  /**
   * Sign in with email/password credentials.
   */
  async signInWithCredentials(username: string, password: string): Promise<ICurrentUser | undefined> {
    try {
      const result = await this.rpcService.call<AuthResult>("auth", { username, password });
      this.signIn(result.accessToken);
      return this.state.currentUser.get();
    } catch (error) {
      console.error("[AuthService] Error signing in with credentials:", error);
      return undefined;
    }
  }

  /**
   * Attempt to restore session from cookie.
   */
  async signInWithSession(): Promise<void> {
    try {
      const result = await this.rpcService.call<AuthResult>("auth-session", {});
      this.signIn(result.accessToken);
    } catch {
      // Silently fail - user just isn't authenticated
    } finally {
      // Mark as ready regardless of outcome
      this.state.setReady();
    }
  }

  /**
   * Sign out the current user.
   */
  async signOut(): Promise<void> {
    if (!this.state.isAuthenticated.get()) {
      return;
    }

    try {
      await this.rpcService.call("sign-out", null);
    } finally {
      // Clear DI state
      this.state.currentUser.set(undefined);
      this.state.accessToken.set(undefined);

      // Clear query cache
      queryClient.clear();

      // Notify callbacks
      this.signOutCallbacks.forEach((cb) => cb());
    }
  }

  /**
   * Set the current public profile being viewed.
   */
  setPublicProfile(handle: string | undefined): void {
    this.state.currentPublicProfile.set(handle);
  }

  /**
   * Update current user data (partial update).
   */
  updateUserData(updates: Partial<ICurrentUser>): void {
    const currentUser = this.state.currentUser.get();
    if (!currentUser) {
      return;
    }

    const updatedUser: ICurrentUser = {
      ...currentUser,
      ...updates,
    };

    this.state.currentUser.set(updatedUser);
  }

  /**
   * Register a callback for sign-in events.
   * @deprecated Use signal reactivity instead
   */
  onSignIn(callback: (user: ICurrentUser) => void): () => void {
    this.signInCallbacks.add(callback);

    // If already authenticated, call synchronously
    // This ensures router.currentUser is set before any navigation
    const currentUser = this.state.currentUser.get();
    if (currentUser) {
      callback(currentUser);
    }

    return () => {
      this.signInCallbacks.delete(callback);
    };
  }

  /**
   * Register a callback for sign-out events.
   * @deprecated Use signal reactivity instead
   */
  onSignOut(callback: () => void): () => void {
    this.signOutCallbacks.add(callback);
    return () => {
      this.signOutCallbacks.delete(callback);
    };
  }

  /**
   * Load current user's profile data from the server.
   * Fetches fresh profile data and updates currentUser with handle, avatarUrl, etc.
   */
  async loadCurrentUserProfile(): Promise<void> {
    if (!this.state.isAuthenticated.get()) return;

    try {
      const profile = await this.rpcService.call<ProfileDataResult>("profileData", undefined);

      // Only update fields that are actually present in the profile
      // to avoid overwriting JWT-decoded values with undefined
      const updates: Partial<ICurrentUser> = {};
      if (profile.handle) updates.handle = profile.handle;
      if (profile.profileImageUrl) updates.avatarUrl = profile.profileImageUrl;
      if (profile.memberSince) updates.memberSinceDate = profile.memberSince;
      if (profile.name) updates.name = profile.name;

      if (Object.keys(updates).length > 0) {
        this.updateUserData(updates);
      }
    } catch (error) {
      console.error("[AuthService] Failed to load profile:", error);
    }
  }

  /**
   * Initialize the auth service.
   * Called after DI container is set up.
   * Attempts to restore session from cookie.
   */
  initialize(): void {
    // Attempt to restore session from cookie
    this.signInWithSession();
  }
}
